import axiosClient from "utils/axios";
import { commaSeparatedEnums, positiveInt } from "utils/list-query";
import { ApiList, ApiOptionList, ApiResponse } from "../../../types/api.types";
import {
  QuotationPdfFormat,
  QuotationSourceType,
  QuotationStatus,
  QuotationType,
} from "features/Quotations/constants/quotation.constants";
import {
  AddQuotationRequest,
  Quotation,
  UpdateQuotationRequest,
} from "features/Quotations/types/quotation.types";

function sanitizeQuotationFilename(code: string): string {
  const s = code.replace(/[^a-zA-Z0-9._-]+/g, "_");
  return (s || "quotation").slice(0, 120);
}

function filenameFromContentDisposition(
  header: string | undefined,
  fallback: string,
): string {
  if (!header) return fallback;
  const star = /filename\*=(?:UTF-8'')?([^;]+)/i.exec(header);
  if (star?.[1]) {
    try {
      return decodeURIComponent(star[1].replace(/["']/g, "").trim());
    } catch {
      /* ignore */
    }
  }
  const quoted = /filename="([^"]+)"/i.exec(header);
  if (quoted?.[1]) return quoted[1];
  const unquoted = /filename=([^;\s]+)/i.exec(header);
  if (unquoted?.[1]) return unquoted[1].replace(/["']/g, "");
  return fallback;
}

export async function downloadQuotationPdf(
  id: number,
  codeForFallback?: string | null,
  format: QuotationPdfFormat = QuotationPdfFormat.NORMAL,
): Promise<void> {
  if (!Number.isInteger(id) || id <= 0) {
    throw new Error("Invalid quotation id");
  }
  const response = await axiosClient.get(`/quotations/${id}/pdf`, {
    responseType: "blob",
    params: { format },
  });

  const base = sanitizeQuotationFilename(
    codeForFallback?.trim() || `quotation-${id}`,
  );
  const suffix = format === QuotationPdfFormat.BOQ ? "-boq" : "";
  const fallback = `${base}${suffix}.pdf`;
  const cd = response.headers["content-disposition"] as string | undefined;
  const filename = filenameFromContentDisposition(cd, fallback);

  const blob = new Blob([response.data], { type: "application/pdf" });
  const url = window.URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.href = url;
  a.download = filename;
  document.body.appendChild(a);
  a.click();
  a.remove();
  window.URL.revokeObjectURL(url);
}

const QUOTATION_LIST_STATUSES = Object.values(QuotationStatus);
const QUOTATION_LIST_SOURCE_TYPES = Object.values(QuotationSourceType);
const QUOTATION_LIST_TYPES = Object.values(QuotationType);

/** Mirrors server `QuotationListFilters` after parsing query params into arrays / numbers. */
export type QuotationListFiltersPayload = {
  status?: string[];
  sourceType?: string[];
  quotationType?: string[];
  clientId?: number;
  sourceId?: number;
};

export type GetQuotationsListParams = {
  page?: number;
  limit?: number;
  search?: string;
  status?: string | string[];
  sourceType?: string | string[];
  quotationType?: string | string[];
  clientId?: number | string;
  sourceId?: number | string;
};

export const getQuotations = async ({
  page,
  limit,
  search,
  status,
  sourceType,
  quotationType,
  clientId,
  sourceId,
}: GetQuotationsListParams = {}) => {
  try {
    const response = await axiosClient.get<ApiResponse<ApiList<Quotation[]>>>(
      "/quotations",
      {
        params: {
          page,
          limit,
          search: search?.trim() || undefined,
          status: commaSeparatedEnums(status, QUOTATION_LIST_STATUSES),
          sourceType: commaSeparatedEnums(
            sourceType,
            QUOTATION_LIST_SOURCE_TYPES,
          ),
          quotationType: commaSeparatedEnums(
            quotationType,
            QUOTATION_LIST_TYPES,
          ),
          clientId: positiveInt(clientId),
          sourceId: positiveInt(sourceId),
        },
      },
    );
    return response.data;
  } catch (err) {
    console.warn("Error fetching Quotations : ", err);
    throw err;
  }
};

export const getQuotationOptions = async ({
  query,
  limit,
  page,
}: {
  query?: string;
  limit?: number;
  page?: number;
}) => {
  try {
    const response = await axiosClient.get<ApiResponse<ApiOptionList>>(
      `/quotations/options`,
      {
        params: {
          query,
          limit,
          page,
        },
      },
    );

    return response.data;
  } catch (err) {
    console.warn("Error fetching Quotation Options : ", err);
    throw err;
  }
};

export const createQuotation = async (payload: AddQuotationRequest) => {
  try {
    const response = await axiosClient.post<ApiResponse<AddQuotationRequest>>(
      "/quotations",
      payload,
    );
    return response.data;
  } catch (err) {
    console.warn("Error creating Quotation : ", err);
    throw err;
  }
};

export const updateQuotation = async (
  id: number,
  payload: UpdateQuotationRequest,
) => {
  try {
    const response = await axiosClient.put<ApiResponse<UpdateQuotationRequest>>(
      `/quotations/${id}`,
      payload,
    );
    return response.data;
  } catch (err) {
    console.warn("Error updating Quotation : ", err);
    throw err;
  }
};

export const getQuotation = async (id: number) => {
  try {
    if (!Number.isInteger(id) || id <= 0) {
      throw new Error("Invalid ID");
    }
    const response = await axiosClient.get<ApiResponse<Quotation>>(
      `/quotations/${id}`,
    );
    return response.data;
  } catch (err) {
    console.warn("Error fetching Quotation : ", err);
    throw err;
  }
};

export const deleteQuotation = async (id: number) => {
  try {
    const response = await axiosClient.delete<ApiResponse<Quotation>>(
      `/quotations/${id}`,
    );
    return response.data;
  } catch (err) {
    console.warn("Error deleting Quotation : ", err);
    throw err;
  }
};

export const sendQuotation = async (id: number) => {
  try {
    const response = await axiosClient.post<ApiResponse<Quotation>>(
      `/quotations/${id}/send`,
    );
    return response.data;
  } catch (err) {
    console.warn("Error sending Quotation : ", err);
    throw err;
  }
};

export const resendQuotation = async (id: number) => {
  try {
    const response = await axiosClient.post<ApiResponse<Quotation>>(
      `/quotations/${id}/resend`,
    );
    return response.data;
  } catch (err) {
    console.warn("Error resending Quotation : ", err);
    throw err;
  }
};

export const acceptQuotation = async (id: number) => {
  try {
    const response = await axiosClient.post<ApiResponse<Quotation>>(
      `/quotations/${id}/accept`,
    );
    return response.data;
  } catch (err) {
    console.warn("Error accepting Quotation : ", err);
    throw err;
  }
};

export const cancelQuotation = async (
  id: number,
  body?: { cancelReason?: string | null },
) => {
  try {
    const response = await axiosClient.post<ApiResponse<Quotation>>(
      `/quotations/${id}/cancel`,
      body ?? {},
    );
    return response.data;
  } catch (err) {
    console.warn("Error cancelling Quotation : ", err);
    throw err;
  }
};

export const rejectQuotation = async (
  id: number,
  body?: { rejectReason?: string | null },
) => {
  try {
    const response = await axiosClient.post<ApiResponse<Quotation>>(
      `/quotations/${id}/reject`,
      body ?? {},
    );
    return response.data;
  } catch (err) {
    console.warn("Error rejecting Quotation : ", err);
    throw err;
  }
};
